import { Fragment, useState, useEffect } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ExclamationCircleIcon, PhotoIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { tournamentSchema, type TournamentData } from '../lib/validation';
import { createTournament, updateTournament, supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';

interface GalleryOption {
  id: string;
  title: string;
  tournament_id: string | null;
}

interface CourseOption {
  id: string;
  name: string;
  city: string | null;
  country: string | null;
}

interface TournamentModalProps {
  isOpen: boolean;
  onClose: () => void;
  tournament?: {
    id: string;
    name: string;
    start_date: string;
    end_date: string;
    status: string;
    capacity: number;
    registration_fee: number;
    prize_pool: number;
    description?: string;
    venue?: string;
    registration_open_date?: string | null;
    registration_close_date?: string | null;
    scoring_method?: string | null;
  } | null;
  onSuccess?: () => void;
}

export default function TournamentModal({
  isOpen,
  onClose,
  tournament,
  onSuccess
}: TournamentModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registrationStartDate, setRegistrationStartDate] = useState('');
  const [registrationEndDate, setRegistrationEndDate] = useState('');
  const [galleries, setGalleries] = useState<GalleryOption[]>([]);
  const [selectedGalleryIds, setSelectedGalleryIds] = useState<Set<string>>(new Set());
  const [courses, setCourses] = useState<CourseOption[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [scoringMethod, setScoringMethod] = useState<string>('stroke_play_net');

  const toDatetimeLocal = (iso: string | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const getFormValues = (): TournamentData => ({
    name: tournament?.name || '',
    start_date: tournament?.start_date || '',
    end_date: tournament?.end_date || '',
    status: (tournament?.status as TournamentData['status']) || 'upcoming',
    capacity: tournament?.capacity || 250,
    registration_fee: tournament?.registration_fee || 50000,
    prize_pool: tournament?.prize_pool || 50000000,
    description: tournament?.description || '',
    venue: tournament?.venue || ''
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset
  } = useForm<TournamentData>({
    resolver: zodResolver(tournamentSchema),
    defaultValues: getFormValues()
  });

  useEffect(() => {
    if (isOpen) {
      reset(getFormValues());
      setError('');
      setRegistrationStartDate(toDatetimeLocal(tournament?.registration_open_date ?? ''));
      setRegistrationEndDate(toDatetimeLocal(tournament?.registration_close_date ?? ''));
      setSelectedCourseId('');
      setScoringMethod('stroke_play_net');
      if (tournament?.id) {
        supabase
          .from('system_settings')
          .select('value')
          .eq('key', `scoring_method_${tournament.id}`)
          .maybeSingle()
          .then(({ data }) => {
            if (data?.value) setScoringMethod(data.value as string);
          });
      }
      fetchGalleries();
      fetchCourses();
      if (tournament?.id) fetchCurrentCourse(tournament.id);
    }
  }, [isOpen, tournament]);

  const fetchCourses = async () => {
    try {
      const { data } = await supabase
        .from('courses')
        .select('id, name, city, country')
        .eq('is_active', true)
        .order('name');
      if (data) setCourses(data as CourseOption[]);
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  const fetchCurrentCourse = async (tournamentId: string) => {
    const { data } = await supabase
      .from('tournament_courses')
      .select('course_id')
      .eq('tournament_id', tournamentId)
      .eq('is_primary', true)
      .maybeSingle();
    setSelectedCourseId(data?.course_id ?? '');
  };

  const saveCourseAssociation = async (tournamentId: string) => {
    const { data: existing } = await supabase
      .from('tournament_courses')
      .select('course_id')
      .eq('tournament_id', tournamentId)
      .eq('is_primary', true)
      .maybeSingle();

    const currentCourseId = existing?.course_id ?? null;

    if (selectedCourseId === (currentCourseId ?? '')) return;

    if (currentCourseId) {
      await supabase
        .from('tournament_courses')
        .delete()
        .eq('tournament_id', tournamentId)
        .eq('is_primary', true);
    }

    if (selectedCourseId) {
      await supabase
        .from('tournament_courses')
        .insert({ tournament_id: tournamentId, course_id: selectedCourseId, is_primary: true });
    }
  };

  const fetchGalleries = async () => {
    try {
      const { data } = await supabase
        .from('content_items')
        .select('id, title, tournament_id')
        .eq('type', 'gallery')
        .order('title');

      if (data) {
        setGalleries(data as GalleryOption[]);
        const linked = new Set(
          data
            .filter((g: GalleryOption) => g.tournament_id === (tournament?.id ?? null) && tournament?.id)
            .map((g: GalleryOption) => g.id)
        );
        setSelectedGalleryIds(linked);
      }
    } catch (err) {
      console.error('Error fetching galleries:', err);
    }
  };

  const saveGalleryAssociations = async (tournamentId: string, originalSelected: Set<string>) => {
    const toLink = [...selectedGalleryIds].filter(id => !originalSelected.has(id));
    const toUnlink = [...originalSelected].filter(id => !selectedGalleryIds.has(id));

    if (toLink.length > 0) {
      await supabase
        .from('content_items')
        .update({ tournament_id: tournamentId })
        .in('id', toLink);
    }
    if (toUnlink.length > 0) {
      await supabase
        .from('content_items')
        .update({ tournament_id: null })
        .in('id', toUnlink);
    }
  };

  const toggleGallery = (id: string) => {
    setSelectedGalleryIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const saveScoringMethod = async (tournamentId: string, method: string) => {
    const key = `scoring_method_${tournamentId}`;
    const { data: existing } = await supabase
      .from('system_settings')
      .select('id')
      .eq('key', key)
      .maybeSingle();
    if (existing) {
      await supabase.from('system_settings').update({ value: method }).eq('key', key);
    } else {
      await supabase.from('system_settings').insert({
        key,
        value: method,
        category: 'tournament',
        description: 'Scoring method for tournament',
        is_public: true,
      });
    }
  };

  const onSubmit = async (data: TournamentData) => {
    try {
      setIsSubmitting(true);
      setError('');

      if (data.prize_pool > 999999999.99) {
        throw new Error('Prize pool cannot exceed ₦999,999,999.99');
      }
      if (data.registration_fee > 999999999.99) {
        throw new Error('Registration fee cannot exceed ₦999,999,999.99');
      }

      const originalSelected = new Set(
        galleries
          .filter(g => g.tournament_id === (tournament?.id ?? null) && tournament?.id)
          .map(g => g.id)
      );

      let tournamentId = tournament?.id ?? '';

      const regDates = {
        registration_open_date: registrationStartDate ? new Date(registrationStartDate).toISOString() : null,
        registration_close_date: registrationEndDate ? new Date(registrationEndDate).toISOString() : null,
      };

      if (tournament) {
        const { error: updateError } = await updateTournament(tournament.id, { ...data, ...regDates });
        if (updateError) throw updateError;
      } else {
        const { data: created, error: createError } = await createTournament({ ...data, ...regDates });
        if (createError) throw createError;
        tournamentId = created?.id ?? '';
      }

      if (tournamentId) {
        await saveScoringMethod(tournamentId, scoringMethod);
        await saveGalleryAssociations(tournamentId, originalSelected);
        await saveCourseAssociation(tournamentId);
      }

      onSuccess?.();
      handleClose();
    } catch (err) {
      console.error('Tournament submission error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg">
                <div className="absolute right-0 top-0 hidden pr-4 pt-4 sm:block">
                  <button
                    type="button"
                    className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-golf-orange focus:ring-offset-2"
                    onClick={handleClose}
                  >
                    <span className="sr-only">Close</span>
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="bg-white">
                  <div className="border-b border-gray-200 bg-golf-sand/30 px-4 py-5 sm:px-6">
                    <Dialog.Title as="h3" className="text-2xl font-display font-bold text-golf-gray">
                      {tournament ? 'Edit Tournament' : 'Create Tournament'}
                    </Dialog.Title>
                  </div>

                  <div className="px-4 py-5 sm:p-6 space-y-6">
                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-3 text-red-600">
                        <ExclamationCircleIcon className="h-5 w-5 flex-shrink-0" />
                        <p className="text-sm">{error}</p>
                      </div>
                    )}

                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Tournament Name <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        {...register('name')}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      />
                      {errors.name && (
                        <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
                      )}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="start_date" className="block text-sm font-medium text-gray-700">
                          Start Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          {...register('start_date')}
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                        />
                        {errors.start_date && (
                          <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="end_date" className="block text-sm font-medium text-gray-700">
                          End Date <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="date"
                          {...register('end_date')}
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                        />
                        {errors.end_date && (
                          <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
                        )}
                      </div>
                    </div>

                    <div>
                      <label htmlFor="status" className="block text-sm font-medium text-gray-700">
                        Status <span className="text-red-500">*</span>
                      </label>
                      <select
                        {...register('status')}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      >
                        <option value="upcoming">Upcoming</option>
                        <option value="ongoing">Ongoing</option>
                        <option value="completed">Completed</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                      {errors.status && (
                        <p className="mt-1 text-sm text-red-600">{errors.status.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="scoring_method" className="block text-sm font-medium text-gray-700">
                        Scoring Method
                      </label>
                      <select
                        id="scoring_method"
                        value={scoringMethod}
                        onChange={(e) => setScoringMethod(e.target.value)}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      >
                        <option value="stroke_play_net">Stroke Play NET</option>
                        <option value="stableford_net">Stableford NET</option>
                      </select>
                      <p className="mt-1 text-xs text-gray-500">
                        {scoringMethod === 'stableford_net'
                          ? 'Points awarded based on score relative to par on each hole'
                          : 'Lowest net score (gross minus handicap) wins'}
                      </p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="capacity" className="block text-sm font-medium text-gray-700">
                          Capacity <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          {...register('capacity', { valueAsNumber: true })}
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                        />
                        {errors.capacity && (
                          <p className="mt-1 text-sm text-red-600">{errors.capacity.message}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor="registration_fee" className="block text-sm font-medium text-gray-700">
                          Registration Fee (N) <span className="text-red-500">*</span>
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          max="999999999.99"
                          {...register('registration_fee', { valueAsNumber: true })}
                          className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                        />
                        {errors.registration_fee && (
                          <p className="mt-1 text-sm text-red-600">{errors.registration_fee.message}</p>
                        )}
                        <p className="mt-1 text-xs text-gray-500">Maximum: N999,999,999.99</p>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="prize_pool" className="block text-sm font-medium text-gray-700">
                        Prize Pool (N) <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        max="999999999.99"
                        {...register('prize_pool', { valueAsNumber: true })}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      />
                      {errors.prize_pool && (
                        <p className="mt-1 text-sm text-red-600">{errors.prize_pool.message}</p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">Maximum: N999,999,999.99</p>
                    </div>

                    <div>
                      <label htmlFor="venue" className="block text-sm font-medium text-gray-700">
                        Venue
                      </label>
                      <input
                        type="text"
                        {...register('venue')}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      />
                      {errors.venue && (
                        <p className="mt-1 text-sm text-red-600">{errors.venue.message}</p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="course_id" className="block text-sm font-medium text-gray-700">
                        Golf Course
                      </label>
                      <select
                        id="course_id"
                        value={selectedCourseId}
                        onChange={(e) => setSelectedCourseId(e.target.value)}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      >
                        <option value="">— No course selected —</option>
                        {courses.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.name}{c.city ? ` · ${c.city}` : ''}{c.country ? `, ${c.country}` : ''}
                          </option>
                        ))}
                      </select>
                      {courses.length === 0 && (
                        <p className="mt-1 text-xs text-gray-400">No active courses found. Add courses in the Courses section first.</p>
                      )}
                    </div>

                    <div className="border-t border-gray-200 pt-6">
                      <h4 className="text-sm font-semibold text-golf-gray mb-4">Registration Timeline</h4>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="registration_start_date" className="block text-sm font-medium text-gray-700">
                            Registration Opens
                          </label>
                          <input
                            type="datetime-local"
                            value={registrationStartDate}
                            onChange={(e) => setRegistrationStartDate(e.target.value)}
                            className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange bg-white text-gray-900"
                          />
                        </div>

                        <div>
                          <label htmlFor="registration_end_date" className="block text-sm font-medium text-gray-700">
                            Registration Closes
                          </label>
                          <input
                            type="datetime-local"
                            value={registrationEndDate}
                            onChange={(e) => setRegistrationEndDate(e.target.value)}
                            className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange bg-white text-gray-900"
                          />
                        </div>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        {...register('description')}
                        rows={4}
                        className="mt-1 block w-full rounded-lg border-gray-300 shadow-sm focus:border-golf-orange focus:ring-golf-orange"
                      />
                      {errors.description && (
                        <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="modern-button w-full sm:w-auto px-8 py-3 text-white sm:ml-3 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Saving...' : tournament ? 'Save Changes' : 'Create Tournament'}
                    </button>
                    <button
                      type="button"
                      className="mt-3 sm:mt-0 w-full sm:w-auto px-8 py-3 bg-white text-gray-700 border border-gray-300 rounded-full hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-golf-orange"
                      onClick={handleClose}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
